import { Injectable } from '@angular/core';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { LocaleService } from './locale.service';

export interface SeoLocalizedValue {
  key: string;
  params?: Array<string | number>;
}

export type SeoValue = string | SeoLocalizedValue | ((context: SeoRouteContext) => string | null | undefined);

export interface SeoRouteContext {
  params: Params;
  queryParams: Params;
  route?: ActivatedRouteSnapshot;
  url: string;
}

export interface RouteSeoData {
  title?: SeoValue;
  description?: SeoValue;
  keywords?: SeoValue;
  robots?: SeoValue;
  ogTitle?: SeoValue;
  ogDescription?: SeoValue;
  ogImage?: SeoValue;
  twitterTitle?: SeoValue;
  twitterDescription?: SeoValue;
  canonical?: SeoValue;
  extraMeta?: MetaDefinition[];
}

export const DEFAULT_SEO_TITLE = 'PUBS.COM - Online Crypto Casino - Sportsbook, Esports, Live Casino';
export const DEFAULT_SEO_DESCRIPTION =
  'PUBS.com is an online crypto casino with sportsbook, esports, live casino, and original games tailored for every player.';
export const DEFAULT_SEO_KEYWORDS =
  'PUBS.com, crypto casino, sportsbook, esports betting, live casino, online gaming, blockchain casino';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private managedMetaKeys = new Set<string>();
  private overrideData?: RouteSeoData;
  private currentStack: RouteSeoData[] = [];
  private currentContext?: SeoRouteContext;

  constructor(
    private title: Title,
    private meta: Meta,
    private locale: LocaleService,
  ) {}

  /**
   * Applies SEO data for the provided route.
   *
   * @param seoStack
   * @param context
   */
  applyRouteSeo(seoStack: RouteSeoData[], context: SeoRouteContext) {
    this.currentStack = seoStack;
    this.currentContext = context;
    const stackWithOverride = this.overrideData ? [...seoStack, this.overrideData] : seoStack;
    this.applyStack(stackWithOverride, context);
  }

  /**
   * Overrides current route SEO with dynamic data (e.g., once async content loads).
   * Pass `undefined` to clear the override.
   *
   * @param data
   */
  overrideWith(data?: RouteSeoData) {
    this.overrideData = data;
    if (!this.currentContext) return;
    const baseStack = data ? [...this.currentStack, data] : [...this.currentStack];
    this.applyStack(baseStack, this.currentContext);
  }

  clearOverrides() {
    this.overrideWith(undefined);
  }

  private applyStack(stack: RouteSeoData[], context: SeoRouteContext) {
    const merged = this.mergeStack(stack);
    this.applySeo(merged, context);
  }

  private mergeStack(stack: RouteSeoData[]): RouteSeoData {
    return stack.reduce<RouteSeoData>(
      (acc, current) => ({
        ...acc,
        ...current,
        extraMeta: [...(acc.extraMeta ?? []), ...(current.extraMeta ?? [])],
      }),
      {},
    );
  }

  private applySeo(data: RouteSeoData, context: SeoRouteContext) {
    const resolvedTitle = this.resolveValue(data.title, context);
    if (resolvedTitle) {
      this.title.setTitle(resolvedTitle);
    }

    const managedDefinitions: MetaDefinition[] = [];

    this.pushMeta(managedDefinitions, 'name', 'description', this.resolveValue(data.description, context));
    this.pushMeta(managedDefinitions, 'name', 'keywords', this.resolveValue(data.keywords, context));
    this.pushMeta(managedDefinitions, 'name', 'robots', this.resolveValue(data.robots, context));

    const ogTitle = this.resolveValue(data.ogTitle ?? data.title, context);
    const ogDescription = this.resolveValue(data.ogDescription ?? data.description, context);

    this.pushMeta(managedDefinitions, 'property', 'og:title', ogTitle);
    this.pushMeta(managedDefinitions, 'property', 'og:description', ogDescription);
    this.pushMeta(managedDefinitions, 'property', 'og:image', this.resolveValue(data.ogImage, context));

    const twitterTitle = this.resolveValue(data.twitterTitle ?? data.title, context);
    const twitterDescription = this.resolveValue(data.twitterDescription ?? data.description, context);
    this.pushMeta(managedDefinitions, 'name', 'twitter:title', twitterTitle);
    this.pushMeta(managedDefinitions, 'name', 'twitter:description', twitterDescription);

    (data.extraMeta ?? []).forEach(def => {
      managedDefinitions.push(def);
    });

    this.syncMetaTags(managedDefinitions);

    const canonicalResolver =
      data.canonical ??
      ((ctx: SeoRouteContext) => {
        const canonicalUrl = this.buildCanonicalUrl(ctx.url);
        return canonicalUrl ?? undefined;
      });

    this.updateCanonical(this.resolveValue(canonicalResolver, context));
  }

  private resolveValue(value: SeoValue | undefined, context: SeoRouteContext): string | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'function') {
      const handled = value(context);
      return handled ?? undefined;
    }

    const localized = this.locale.getValue(value.key, ...(value.params ?? []));
    return localized || undefined;
  }

  private pushMeta(
    defs: MetaDefinition[],
    attrName: 'name' | 'property',
    attrValue: string,
    content: string | undefined,
  ) {
    if (!content) return;
    defs.push({ [attrName]: attrValue, content });
  }

  private metaKey(def: MetaDefinition): string {
    if (def.name) return `name:${def.name}`;
    if (def.property) return `prop:${def.property}`;
    return `${def.httpEquiv ?? 'meta'}:${def.content}`;
  }

  private syncMetaTags(definitions: MetaDefinition[]) {
    const nextKeys = new Set<string>();

    definitions.forEach(def => {
      const key = this.metaKey(def);
      nextKeys.add(key);
      this.meta.updateTag(
        def,
        def.name ? `name="${def.name}"` : def.property ? `property="${def.property}"` : undefined,
      );
    });

    this.managedMetaKeys.forEach(key => {
      if (!nextKeys.has(key)) {
        if (key.startsWith('name:')) {
          const name = key.replace('name:', '');
          this.meta.removeTag(`name="${name}"`);
        } else if (key.startsWith('prop:')) {
          const property = key.replace('prop:', '');
          this.meta.removeTag(`property="${property}"`);
        }
      }
    });

    this.managedMetaKeys = nextKeys;
  }

  private buildCanonicalUrl(path: string): string | undefined {
    if (typeof window === 'undefined') return undefined;
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const sanitizedPath = normalizedPath.replace(/\/{2,}/g, '/');
    return `${window.location.origin}${sanitizedPath === '/' ? '' : sanitizedPath}`;
  }

  private updateCanonical(canonicalUrl?: string) {
    let linkEl = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalUrl) {
      if (linkEl) {
        linkEl.parentNode?.removeChild(linkEl);
      }
      return;
    }

    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.setAttribute('rel', 'canonical');
      document.head.appendChild(linkEl);
    }
    linkEl.setAttribute('href', canonicalUrl);
  }
}
